<template>
  <VanPopup
    :show="show"
    @update:show="emit('update:show', $event)"
    position="bottom"
    round
    safe-area-inset-bottom
  >
    <div class="w-full flex flex-col gap-2 py-4">
      <VanCellGroup inset>
        <VanCell @click="router.push({ name: 'mateWishEdit' })" is-link>
          <template #title>
            <div class="flex items-center gap-2">
              <i class="iconify mingcute--magic-2-line" />
              <div>{{ $t("mateWish.name") }}</div>
            </div>
          </template>
        </VanCell>
        <VanCell @click="router.push({ name: 'draw' })" is-link>
          <template #title>
            <div class="flex items-center gap-2">
              <i class="iconify mingcute--paint-brush-line" />
              <div>{{ $t("Buttons.handDraw") }}</div>
            </div>
          </template>
        </VanCell>
        <VanUploader
          v-model="imageUploadList"
          :accept="IMAGE_ACCEPT"
          :after-read="(file:any) => onMediaUpload(file, 1)"
          :preview-image="false"
          class="gallery-uploader w-full"
        >
          <VanCell>
            <template #title>
              <div class="flex items-center gap-2">
                <i class="iconify mingcute--pic-line" />
                <div>{{ $t("Buttons.uploadImage") }}</div>
              </div>
            </template>
          </VanCell>
        </VanUploader>
        <VanUploader
          v-model="videoUploadList"
          :accept="VIDEO_ACCEPT"
          :after-read="(file:any) => onMediaUpload(file, 2)"
          :max-size="MAX_VIDEO_SIZE"
          :preview-image="false"
          class="w-full gallery-uploader"
          @oversize="onOversize(MAX_VIDEO_SIZE)"
        >
          <VanCell>
            <template #title>
              <div class="flex items-center gap-2">
                <i class="iconify mingcute--video-camera-line" />
                <div>{{ $t("Buttons.uploadVideo") }}</div>
              </div>
            </template>
          </VanCell>
        </VanUploader>

        <VanUploader
          v-model="audioUploadList"
          :accept="AUDIO_ACCEPT"
          :after-read="(file:any) => onMediaUpload(file, 3)"
          :max-size="MAX_AUDIO_SIZE"
          :preview-image="false"
          @oversize="onOversize(MAX_AUDIO_SIZE)"
          class="w-full gallery-uploader"
        >
          <VanCell>
            <template #title>
              <div class="flex items-center gap-2">
                <i class="iconify mingcute--music-line" />
                <div>{{ $t("Buttons.uploadAudio") }}</div>
              </div>
            </template>
          </VanCell>
        </VanUploader>
      </VanCellGroup>
      <VanCellGroup inset>
        <VanCell
          :title="$t('Buttons.cancel')"
          clickable
          @click="emit('update:show', false)"
        />
      </VanCellGroup>
    </div>
  </VanPopup>
</template>

<script setup lang="ts">
import { useScopedMateId } from "@/composable/useScopedMateId";
import api from "@/modules/api";
import {
  IMAGE_ACCEPT,
  MAX_AUDIO_SIZE,
  MAX_VIDEO_SIZE,
  VIDEO_ACCEPT,
  AUDIO_ACCEPT,
} from "@/modules/constant";
import { useMedia } from "@/stores/media";
import { MediaUploadFileItem } from "@/types/component";
import { MediaResourceType, MediaType, ModuleEntityType } from "@/types/entity";
import { showFailToast, showLoadingToast, showSuccessToast } from "vant";
import { ref } from "vue";
import { useI18n } from "vue-i18n";
import { useRouter } from "vue-router";

const props = defineProps<{
  show: boolean;
  mateId: number;
  entityId?: number;
  entityType?: ModuleEntityType;
}>();

const emit = defineEmits<{
  (e: "update:show", show: boolean): void;
  (e: "finished"): void;
}>();

const imageUploadList = ref<MediaUploadFileItem[]>([]);
const videoUploadList = ref<MediaUploadFileItem[]>([]);
const audioUploadList = ref<MediaUploadFileItem[]>([]);

const { t } = useI18n();
const mediaStore = useMedia();
const router = useRouter();

const onMediaUpload = async (
  file: MediaUploadFileItem,
  type: MediaResourceType
) => {
  const scopedMateIdFunc = useScopedMateId(async () => {
    const toast = showLoadingToast({
      duration: 0,
      message: t("Message.uploading"),
    });
    try {
      if (props.entityId && props.entityType) {
        // 模块文件保存
        let mediaType: MediaType = 0;
        if (type === 3) {
          // 音频
          mediaType = 1;
        } else if (type === 2) {
          // 视频
          mediaType = 9;
        }
        const { mediaId } = await mediaStore.uploadMediaItem(
          file,
          props.mateId,
          mediaType
        );
        await api.createOneModuleMedia({
          entityId: props.entityId,
          entityType: props.entityType,
          mediaId,
          type,
        });
      } else {
        let mediaType: MediaType = 5;
        if (type === 3) {
          mediaType = 1;
        }
        const { mediaId } = await mediaStore.uploadMediaItem(
          file,
          props.mateId,
          mediaType
        );
        if (type === 3) {
          await api.createOneMateMedia({ mediaId, type });
        }
      }
      toast.close();
      showSuccessToast(t("Message.uploadSuccessful"));
      emit("update:show", false);
      emit("finished");
    } finally {
      toast.close();
      imageUploadList.value = [];
      videoUploadList.value = [];
    }
  }, props.mateId);
  await scopedMateIdFunc();
};

const onOversize = (size: number) => {
  showFailToast(t("Message.oversize", { size: size / 1024 / 1024 }));
};
</script>
